import type { ReactNode } from 'react';

/**
 * Envolve o conteúdo de um shell com a sua navegação.
 *
 * Distingue-se do `AppShell`: **este é o cromo de um shell** (a barra de separadores, a sidebar),
 * enquanto o `AppShell` é a estrutura de acessibilidade do **documento inteiro** — *skip link*,
 * `<main>`, landmarks.
 *
 * A separação nasceu de uma falha concreta. O `AppShell` estava montado dentro de cada shell, mas
 * os shells exigem sessão (`useAuth`, que só o M1 implementa). O resultado é que `/`, `/login` e o
 * 404 — as **únicas** rotas que um visitante alcança hoje — não tinham *skip link* nenhum. O
 * `axe-core` passava, porque uma página vazia não tem nada de errado; foi o E2E de teclado que o
 * apanhou, ao não encontrar elemento focável nenhum.
 *
 * A acessibilidade do documento não pode depender de estar autenticado. O `AppShell` subiu para o
 * `App`; aqui fica só o que é específico de cada shell.
 */
export interface ShellChromeProps {
  /** Cabeçalho contextual partilhado pelos shells, quando aplicável. */
  header?: ReactNode;
  /** *Bottom tabs* ou *sidebar*, conforme o shell. */
  nav?: ReactNode;
  children: ReactNode;
}

export function ShellChrome({ header, nav, children }: ShellChromeProps) {
  return (
    // `min-h-dvh` e não `min-h-full`: o `<main>` do AppShell cresce por `flex-1`, sem altura
    // definida, e um percentual sobre ele não resolve — em páginas curtas a barra de separadores
    // subia para o meio da tela (T1.17). O AppShell não tem cabeçalho próprio acima dos shells,
    // logo a viewport dinâmica é a altura certa; `dvh` acompanha a barra do browser no mobile.
    <div className="flex min-h-dvh flex-col">
      {header}
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      {nav}
    </div>
  );
}
